import { useEffect, useState } from "react";
import { Crown, Check, X } from "lucide-react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useYacht } from "../context/YachtContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";

/**
 * Shown automatically after sign-in when a yacht ownership transfer is
 * awaiting the user's decision. Accepting makes them the yacht's Owner and
 * removes the previous owner entirely; declining leaves everything as-is.
 */
export default function TransferModal() {
  const { user } = useAuth();
  const { refresh: refreshYachts } = useYacht();
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!user) { setQueue([]); return; }
    try {
      const { data } = await api.get("/yacht-transfers/pending");
      setQueue(data || []);
    } catch { /* non-fatal */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.id]);

  useEffect(() => {
    if (!user) return;
    const onFocus = () => { load(); };
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line
  }, [user?.id]);

  if (!queue.length) return null;
  const current = queue[0];

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/yacht-transfers/${current.id}/accept`);
      toast.success(`You are now the Owner of ${current.yacht_name}.`);
      setQueue((q) => q.slice(1));
      refreshYachts();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await api.post(`/yacht-transfers/${current.id}/decline`);
      toast.info(`Declined the ownership transfer of ${current.yacht_name}.`);
      setQueue((q) => q.slice(1));
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => { /* must accept or decline */ }}>
      <DialogContent
        className="max-w-md [&>button:last-of-type]:hidden"
        data-testid="transfer-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500 text-white">
              <Crown className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle>Yacht ownership transfer</DialogTitle>
              <DialogDescription className="text-xs">
                {queue.length > 1
                  ? `${queue.length} transfers awaiting your decision`
                  : "Awaiting your decision"}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 text-sm leading-relaxed">
          <p>
            <strong>{current.initiator_name || "Someone"}</strong> wants to transfer ownership of{" "}
            <strong>{current.yacht_name}</strong> to you.
          </p>
          <p className="text-zinc-600 text-[13px]">
            If you accept, you become the <strong>Owner</strong> of this yacht with full control —
            and the previous owner loses access to it entirely. Nothing changes if you decline.
          </p>
          <p className="text-zinc-600 text-[13px]" data-testid="transfer-crew-note">
            {current.include_crew === false
              ? "The crew is NOT transferring: all crew members and their HR records will be removed when you accept. The yacht's supplier network stays."
              : "The current crew and their records transfer with the yacht."}
          </p>
        </div>

        <DialogFooter className="flex sm:justify-between gap-2 mt-2">
          <Button
            type="button"
            variant="outline"
            onClick={decline}
            disabled={busy}
            data-testid="transfer-decline"
          >
            <X className="h-4 w-4 mr-2" />
            Decline
          </Button>
          <Button
            type="button"
            onClick={accept}
            disabled={busy}
            className="bg-ink hover:bg-black"
            data-testid="transfer-accept"
          >
            <Check className="h-4 w-4 mr-2" />
            Accept ownership
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
